export interface ComposerPosition {
  left: number;
  top: number;
}

export interface ComposerPositionInput {
  node: {
    canvasPosition: { x: number; y: number };
    size: { width: number; height: number };
  };
  flowViewport: { x: number; y: number; zoom: number };
  canvasViewport: { width: number; height: number };
  card: { width: number; height: number };
}

export interface ComposerPlacement extends ComposerPosition {
  width: number;
  height: number;
}

export interface ComposerFlowNodePlacement {
  position: { x: number; y: number };
  width: number;
  height: number;
}

const margin = 12;
const nodeGap = 18;
const minimumComposerHeight = 150;
const minimumComposerWidth = 360;

export function clampComposerPosition(
  position: ComposerPosition,
  viewport: { width: number; height: number },
  card: { width: number; height: number }
): ComposerPosition {
  return {
    left: Math.max(margin, Math.min(position.left, Math.max(margin, viewport.width - card.width - margin))),
    top: Math.max(margin, Math.min(position.top, Math.max(margin, viewport.height - card.height - margin)))
  };
}

export function defaultComposerPosition({
  node,
  flowViewport,
  canvasViewport,
  card
}: ComposerPositionInput): ComposerPosition {
  const { left, top } = defaultComposerPlacement({ node, flowViewport, canvasViewport, card });
  return { left, top };
}

export function defaultComposerPlacement({
  node,
  flowViewport,
  canvasViewport,
  card
}: ComposerPositionInput): ComposerPlacement {
  const zoom = flowViewport.zoom;
  const nodeLeft = flowViewport.x + node.canvasPosition.x * zoom;
  const nodeTop = flowViewport.y + node.canvasPosition.y * zoom;
  const nodeWidth = node.size.width * zoom;
  const right = nodeLeft + nodeWidth + nodeGap;
  const constrainedCard = {
    width: Math.min(card.width, canvasViewport.width - margin * 2),
    height: Math.min(card.height, Math.max(0, canvasViewport.height - margin * 2))
  };
  const rightAvailableWidth = canvasViewport.width - margin - right;
  const rightWidth = Math.min(constrainedCard.width, rightAvailableWidth);
  if (rightWidth >= Math.min(minimumComposerWidth, constrainedCard.width)) {
    const rightCard = { ...constrainedCard, width: rightWidth };
    return {
      left: right,
      top: clampComposerPosition({ left: right, top: nodeTop }, canvasViewport, rightCard).top,
      ...rightCard
    };
  }

  const { width } = constrainedCard;
  const left = Math.max(margin, Math.min(nodeLeft, canvasViewport.width - width - margin));
  const availableAboveHeight = nodeTop - nodeGap - margin;
  if (availableAboveHeight >= minimumComposerHeight) {
    const height = Math.min(constrainedCard.height, availableAboveHeight);
    return { left, top: nodeTop - nodeGap - height, width, height };
  }

  const below = nodeTop + node.size.height * zoom + nodeGap;
  const availableBelowHeight = canvasViewport.height - below - margin;
  if (availableBelowHeight >= minimumComposerHeight) {
    const height = Math.min(constrainedCard.height, availableBelowHeight);
    return { left, top: below, width, height };
  }

  const position = clampComposerPosition({ left: right, top: nodeTop }, canvasViewport, constrainedCard);
  return { ...position, ...constrainedCard };
}

/**
 * React Flow stores node coordinates before its viewport transform. Convert
 * the visible placement back into flow coordinates, but retain the card's
 * flow-space size so the composer scales together with every other node.
 */
export function composerPlacementToFlowNode(input: {
  placement: ComposerPlacement;
  flowViewport: { x: number; y: number; zoom: number };
}): ComposerFlowNodePlacement {
  const zoom = input.flowViewport.zoom || 1;
  return {
    position: {
      x: (input.placement.left - input.flowViewport.x) / zoom,
      y: (input.placement.top - input.flowViewport.y) / zoom
    },
    width: input.placement.width,
    height: input.placement.height
  };
}

export function offsetComposerPosition(input: {
  base: ComposerPosition;
  offset: ComposerPosition;
  delta: { x: number; y: number };
  viewport: { width: number; height: number };
  card: { width: number; height: number };
}): ComposerPosition {
  const absolute = clampComposerPosition({
    left: input.base.left + input.offset.left + input.delta.x,
    top: input.base.top + input.offset.top + input.delta.y
  }, input.viewport, input.card);
  return {
    left: absolute.left - input.base.left,
    top: absolute.top - input.base.top
  };
}
